/**
 * Turn a WebRTC stats report into the numbers the studio prints over the camera.
 *
 * Pure and separate from the hook because the mistake it exists to prevent is
 * invisible from the outside. Simulcast publishes three video layers and each
 * gets its own `outbound-rtp` report: a loop sharing one `lastBytes` across all
 * three subtracts one layer's cumulative count from another's, so the "bitrate"
 * is noise and `resolution` is whichever layer the iterator yielded last —
 * usually the smallest, so a healthy 1080p broadcast reads 320x180.
 *
 * That readout is the only thing that tells a host their stream is quietly being
 * throttled, so being wrong defeats the point of showing it.
 */

export type Wire = {
  resolution: string;
  fps: string;
  bitrate: string;
  /** `qualityLimitationReason`: "bandwidth", "cpu" or "none". */
  limitedBy: string;
  rtt: string;
};

/** Cumulative bytes across every layer, and when they were read. */
export type WireSample = { bytesSent: number; atMs: number };

type Report = Record<string, unknown>;

const num = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export function summarizeVideoStats(
  reports: Iterable<Report>,
  previous: WireSample | null,
  nowMs: number,
): { wire: Partial<Wire>; sample: WireSample } | null {
  let bytesSent = 0;
  let top: Report | null = null;
  let rttSeconds: number | null = null;
  let sawVideo = false;

  for (const report of reports) {
    if (report.type === "outbound-rtp" && report.kind === "video") {
      sawVideo = true;
      // Every layer counts toward what is actually leaving the device.
      bytesSent += num(report.bytesSent);
      // Only the biggest one describes the picture a good connection gets.
      if (!top || num(report.frameWidth) > num(top.frameWidth)) top = report;
    }

    if (report.type === "candidate-pair") {
      // Several pairs can be "succeeded"; only one is nominated, and that is the
      // one carrying the media.
      const usable = report.nominated === true || report.state === "succeeded";
      const rtt = report.currentRoundTripTime;
      if (usable && typeof rtt === "number" && (report.nominated === true || rttSeconds === null)) {
        rttSeconds = rtt;
      }
    }
  }

  if (!sawVideo && rttSeconds === null) return null;

  const wire: Partial<Wire> = {};

  if (top) {
    const width = num(top.frameWidth);
    const height = num(top.frameHeight);
    wire.resolution = width && height ? `${width}×${height}` : "?";

    const fps = top.framesPerSecond;
    wire.fps = typeof fps === "number" ? `${Math.round(fps)}fps` : "?";
    wire.limitedBy = String(top.qualityLimitationReason ?? "none");
  }

  if (sawVideo) {
    const elapsedMs = previous ? nowMs - previous.atMs : 0;
    const deltaBytes = previous ? bytesSent - previous.bytesSent : 0;

    // A negative delta means the layer set changed under us, so there is no
    // honest rate to report for this tick.
    wire.bitrate =
      previous && elapsedMs > 0 && deltaBytes >= 0 ? `${Math.round((deltaBytes * 8) / elapsedMs)} kbps` : "measuring";
  }

  if (rttSeconds !== null) wire.rtt = `${Math.round(rttSeconds * 1000)} ms`;

  return { wire, sample: { bytesSent, atMs: nowMs } };
}

/** Merge a partial reading over the last one, so a missing field does not blank. */
export function mergeWire(current: Wire | null, next: Partial<Wire>): Wire {
  return {
    resolution: next.resolution ?? current?.resolution ?? "?",
    fps: next.fps ?? current?.fps ?? "?",
    bitrate: next.bitrate ?? current?.bitrate ?? "?",
    limitedBy: next.limitedBy ?? current?.limitedBy ?? "none",
    rtt: next.rtt ?? current?.rtt ?? "?",
  };
}

/**
 * The widest rear lens a device offers, by label, or null to keep the default.
 *
 * Labels are the only signal a browser gives — there is no field-of-view
 * constraint, the ultra-wide is just another deviceId with a name. So this
 * matches what vendors actually call them and returns null when nothing matches,
 * because a wrong lens is worse than the default: the host has to notice and
 * undo it mid-show.
 *
 * Front cameras are excluded explicitly. Several are labelled "wide", and
 * pointing the show at the host's face is the one outcome this must not produce.
 */
export function preferWideCamera(devices: { deviceId: string; label: string }[], currentId: string): string | null {
  const front = /front|face|user|selfie|truedepth/i;
  const wide = /ultra[\s-]?wide|wide[\s-]?angle|0\.5/i;

  const candidate = devices.find((d) => d.label && !front.test(d.label) && wide.test(d.label));

  if (!candidate || candidate.deviceId === currentId) return null;
  return candidate.deviceId;
}
